import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Card, Form, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiSave, FiX } from "react-icons/fi";
import Swal from 'sweetalert2';

export default function RoleEdit() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [permissions, setPermissions] = useState([]);
    const [formData, setFormData] = useState({
        name: "",
        permissions: []
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchRoleData();
    }, [id]);

    const fetchRoleData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            // Your API returns: { role, permissions, role_permissions }
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/roles/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (res.data) {
                // Set all permissions list
                if (res.data.permissions) {
                    setPermissions(res.data.permissions);
                }
                
                // Set role data with its permissions
                if (res.data.role) {
                    setFormData({
                        name: res.data.role.name || "",
                        permissions: res.data.role_permissions || [] // role_permissions contains the permission names
                    });
                }
            }
        } catch (err) {
            console.error("Error fetching role data:", err);
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: err.response?.data?.message || t('Failed to load role data')
            });
            navigate("/users-roles");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handlePermissionChange = (permissionName) => {
        setFormData(prev => {
            const updatedPermissions = prev.permissions.includes(permissionName)
                ? prev.permissions.filter(p => p !== permissionName)
                : [...prev.permissions, permissionName];
            return { ...prev, permissions: updatedPermissions };
        });
    };

    const handleSubmit = async () => {
        // Validate form
        if (!formData.name.trim()) {
            setErrors({ name: t("Role name is required") });
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/roles/${id}`,
                {
                    name: formData.name,
                    permissions: formData.permissions
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: t('Success'),
                    text: t('Role updated successfully'),
                    timer: 2000,
                    showConfirmButton: false
                });
                navigate("/users-roles");
            } else {
                throw new Error(response.data.message || 'Failed to update role');
            }
        } catch (err) {
            console.error("Error updating role:", err);
            
            // Handle validation errors from server
            if (err.response?.data?.errors) {
                const validationErrors = {};
                Object.keys(err.response.data.errors).forEach(key => {
                    validationErrors[key] = err.response.data.errors[key][0];
                });
                setErrors(validationErrors);
            }
            
            Swal.fire({
                icon: 'error',
                title: t('Error'),
                text: err.response?.data?.message || t('Failed to update role')
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row className="justify-content-center">
                <Col xl={11}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="p-4">
                            <Form onSubmit={(e) => e.preventDefault()}>
                                {/* Role Name Input */}
                                <Row className="align-items-center mb-4">
                                    <Col md={2}>
                                        <Form.Label className="fw-bold mb-0">
                                            {t("Role Name")} <span className="text-danger">*</span>
                                        </Form.Label>
                                    </Col>
                                    <Col md={10}>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!errors.name}
                                            style={{ borderRadius: '8px' }}
                                            placeholder={t("Enter role name")}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.name}
                                        </Form.Control.Feedback>
                                    </Col>
                                </Row>

                                <h5 className="mb-3 fw-bold" style={{ color: '#444' }}>
                                    {t("Permissions")}
                                </h5>

                                {/* Permissions Table */}
                                <div className="border rounded overflow-hidden">
                                    <Table hover responsive className="mb-0 custom-permission-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="py-3 ps-4 text-uppercase small fw-bold" style={{ width: '80%' }}>
                                                    {t("MODULE")}
                                                </th>
                                                <th className="py-3 text-uppercase small fw-bold text-center">
                                                    {t("MANAGE")}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {permissions.map((permission) => (
                                                <tr key={permission.id}>
                                                    <td className="ps-4 py-3 text-muted">
                                                        {permission.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                                    </td>
                                                    <td className="text-center py-3">
                                                        <Form.Check
                                                            type="checkbox"
                                                            className="custom-checkbox-input"
                                                            checked={formData.permissions.includes(permission.name)}
                                                            onChange={() => handlePermissionChange(permission.name)}
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Form Actions */}
                                <div className="d-flex gap-2 mt-4">
                                    <ButtonComponent
                                        className="btn btn-primary px-4"
                                        text={<><FiSave className="me-2" /> {submitting ? t("Updating...") : t("Update Role")}</>}
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                    />
                                    <ButtonComponent
                                        className="btn btn-info border px-4"
                                        text={<><FiX className="me-2" /> {t("Cancel")}</>}
                                        onClick={() => navigate("/users-roles")}
                                    />
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Custom CSS */}
            <style jsx>{`
                .custom-permission-table thead th {
                    background-color: #f8f9fa;
                    border-bottom: 1px solid #dee2e6;
                    letter-spacing: 0.5px;
                }
                .custom-permission-table td {
                    border-bottom: 1px solid #f0f0f0;
                    vertical-align: middle;
                    font-size: 15px;
                }
                .form-check-input:checked {
                    background-color: #a855f7;
                    border-color: #a855f7;
                }
                .form-control:focus {
                    box-shadow: none;
                    border-color: #dee2e6;
                }
                .form-check-input:focus {
                    box-shadow: none;
                    border-color: #a855f7;
                }
            `}</style>
        </PageLayout>
    );
}